import { useIsMutating } from '@tanstack/react-query';
import { useForm } from 'formoid';
import { FormEvent } from 'react';
import { z } from 'zod';
import {
  CheckboxField,
  InputField,
  PasswordField,
  PasswordTooltipContent,
  Tooltip,
  Button,
  Link,
} from '~/common/components';
import {
  PASSWORD_MIN_LENGTH,
  customSchema,
  fromZodSchema,
  maxLengthMessage,
  minLengthMessage,
  propagateBackendValidationErrors,
} from '~/common/utils';
import { routes } from '~/constants';
import { useSignup } from '~/root/Auth';
import { qk } from '~/root/query-keys';
import { OauthProviders } from '../OauthProviders';

const initialValues = {
  fullName: '',
  email: '',
  password: '',
  receiveOffers: false,
  consents: false,
};

const validators = () =>
  fromZodSchema({
    fullName: [
      'Name',
      customSchema.onlyLatinLetters
        .min(2, minLengthMessage('Name', 2))
        .max(50, maxLengthMessage('Name', 50)),
    ],
    password: ['Password', customSchema.password],
    email: ['Email', customSchema.email],
    receiveOffers: null,
    consents: [
      'Consents',
      z.boolean().refine((value) => value, 'You must accept our Terms of Use'),
    ],
  });

export const SignupForm = () => {
  const { mutateAsync: signup, isLoading } = useSignup();
  const isOauthMutating = useIsMutating({ mutationKey: [qk.oauthLogin] }) > 0;

  const { fieldProps, handleSubmit, setErrors, errors } = useForm({
    initialValues,
    validationStrategy: 'onBlur',
    validators,
  });

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit(({ consents, ...values }) => {
      return signup(values).catch((error: unknown) => {
        propagateBackendValidationErrors({ error, setErrors, formErrors: errors });
      });
    });
  };

  const signupLoading = isLoading || isOauthMutating;

  return (
    <>
      <h2 className="font-brand-t2b mb-1">Sign up</h2>
      <p className="font-brand-b3 mb-3 text-text-400">
        Already have an account?&nbsp;
        <Link to={routes.login}>Log in</Link>
      </p>

      <OauthProviders isSignup className="mb-3" />

      <form className="space-y-3" onSubmit={submit}>
        <InputField
          {...fieldProps('fullName')}
          type="text"
          title="Full name"
          placeholder="Enter your full name"
        />

        <InputField
          {...fieldProps('email')}
          type="email"
          title="Email"
          placeholder="Enter your email"
        />

        <Tooltip content={<PasswordTooltipContent />} placement="right" color="primary">
          <PasswordField
            {...fieldProps('password')}
            title="Password"
            placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
          />
        </Tooltip>

        <CheckboxField
          {...fieldProps('consents')}
          title={
            <>
              I agree to the{' '}
              <Link href={routes.public.documents.terms_and_conditions}>Terms and Conditions</Link>,{' '}
              <Link href={routes.public.documents.privacy_policy}>Privacy Policy</Link> and{' '}
              <Link href={routes.public.documents.data_processing_agreement}>
                Data Processing Agreement.
              </Link>
            </>
          }
        />

        <CheckboxField
          {...fieldProps('receiveOffers')}
          title="I'd like to receive occasional emails with information on new services and offers"
        />

        {/* TODO: Check is it needed here to have OAuth loading state */}
        <Button
          className="w-full"
          size="m"
          color="secondary-navy"
          loading={signupLoading}
          type="submit"
        >
          Continue
        </Button>
      </form>
    </>
  );
};
